import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/theme.css";
import "./Login.css";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
  signInWithEmailAndPassword,
} from "firebase/auth";
import BankIDLogin from "../components/BankIDLogin";
import PasswordInput from "../components/PasswordInput"; // ⬅️ new

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [manualSignup, setManualSignup] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);

  const [sending, setSending] = useState(false);
  const [signingIn, setSigningIn] = useState(false);

  const [userDetails, setUserDetails] = useState({ name: "", email: "" });
  const [emailLogin, setEmailLogin] = useState({ email: "", password: "" });

  // banner after we send the password setup email
  const justCreated = Boolean(location.state?.justCreated);
  const createdEmail = location.state?.email || "";

  // where to go after any successful login
  const getPostLoginRedirect = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("redirect") || location.state?.from || "/myaccount";
  };
  const redirectBack = () => navigate(getPostLoginRedirect(), { replace: true });

  useEffect(() => {
    if (justCreated) {
      setManualSignup(false);
      setShowEmailLogin(true);
      setEmailLogin((s) => ({ ...s, email: createdEmail }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [justCreated, createdEmail]);

  // Google sign-in
  const handleGoogleLogin = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      await signInWithPopup(auth, provider);
      redirectBack();
    } catch (err) {
      console.error("Google Login Failed:", err);
      alert(t("login.error_google") || "Google sign-in failed. Please try again.");
    }
  };

  // Manual signup -> create temp account -> send reset -> sign out -> show banner
  const handleManualSignup = async () => {
    const name = (userDetails.name || "").trim();
    const email = (userDetails.email || "").trim();

    if (!name || !email) {
      alert(t("login.fill_all_fields") || "Please fill in all fields.");
      return;
    }

    const auth = getAuth();
    const tempPassword = Math.random().toString(36).slice(-10) + "A1!";

    try {
      setSending(true);

      const cred = await createUserWithEmailAndPassword(auth, email, tempPassword);
      if (name) await updateProfile(cred.user, { displayName: name });

      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };
      await sendPasswordResetEmail(auth, email, actionCodeSettings);

      await signOut(auth);

      setUserDetails({ name: "", email: "" });
      setManualSignup(false);
      navigate("/login", { state: { justCreated: true, email }, replace: true });
    } catch (err) {
      if (err?.code === "auth/email-already-in-use") {
        try {
          const actionCodeSettings = {
            url: `${window.location.origin}/login`,
            handleCodeInApp: false,
          };
          await sendPasswordResetEmail(auth, email, actionCodeSettings);
          await signOut(auth);

          setUserDetails({ name: "", email: "" });
          setManualSignup(false);
          navigate("/login", { state: { justCreated: true, email }, replace: true });
        } catch (resetErr) {
          console.error("Send reset error:", resetErr);
          alert(t("login.error_reset_send") || "Could not send the email. Please try again.");
        }
      } else {
        console.error("Manual signup error:", err);
        alert(t("login.error_manual_signup") || "Could not create the account. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  // Email + password sign-in
  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    const email = emailLogin.email.trim();
    const password = emailLogin.password;

    if (!email || !password) {
      alert(t("login.fill_all_fields") || "Please fill in all fields.");
      return;
    }

    setSigningIn(true);
    try {
      const auth = getAuth();
      await signInWithEmailAndPassword(auth, email, password);
      redirectBack();
    } catch (err) {
      console.error("Email sign-in error:", err);
      alert(
        t("login.error_email_signin") ||
          "We couldn't sign you in with that email/password. Please try again."
      );
    } finally {
      setSigningIn(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!emailLogin.email) {
      alert(t("login.enter_email_first") || "Please enter your email first.");
      return;
    }
    try {
      const auth = getAuth();
      await sendPasswordResetEmail(auth, emailLogin.email, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      });
      alert(
        t("login.reset_sent_banner", { email: emailLogin.email }) ||
          `We sent a reset link to ${emailLogin.email}`
      );
    } catch (err) {
      console.error("Forgot password error:", err);
      alert(t("login.error_reset_send") || "Could not send the email. Please try again.");
    }
  };

  return (
    <div className="login-page-wrapper">
    <div className="login-container">
      <h1>{t("login.title")}</h1>

      {justCreated && (
        <div className="notice success" style={{ marginBottom: 12 }}>
          {t("login.reset_sent_banner", { email: createdEmail })}
        </div>
      )}

      {/* OAuth buttons */}
      <button className="btn btn-google" type="button" onClick={handleGoogleLogin}>
        {t("login.google_sign_in")}
      </button>

       {/*<BankIDLogin onSuccess={() => redirectBack()} className="btn btn-bankid" />*/}

      {/* Toggles */}
      <div className="login-toggles">
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            setManualSignup((v) => !v);
            if (showEmailLogin) setShowEmailLogin(false);
          }}
        >
          {t("login.manual_signup")}
        </button>

        <button
          type="button"
          className="btn btn-outline"
          onClick={() => {
            setShowEmailLogin((v) => !v);
            if (manualSignup) setManualSignup(false);
          }}
        >
          {t("login.email_login_toggle") || "Sign in with Email"}
        </button>
      </div>

      {/* Manual signup form */}
      {manualSignup && (
        <div className="manual-signup-form card-section">
          <input
            type="text"
            placeholder={t("login.name")}
            value={userDetails.name}
            onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
            required
            className="ui-input"
          />
          <input
            type="email"
            placeholder={t("login.email")}
            value={userDetails.email}
            onChange={(e) => setUserDetails({ ...userDetails, email: e.target.value })}
            required
            className="ui-input"
          />

          <button
            type="button"
            className="btn btn-success"
            onClick={handleManualSignup}
            disabled={sending}
          >
            {sending ? t("login.sending_reset") : t("login.create_account")}
          </button>

          <p className="hint">{t("login.create_flow_hint")}</p>
        </div>
      )}

      {/* Email + Password login form */}
      {showEmailLogin && (
        <form className="email-login-form card-section" onSubmit={handleEmailSignIn}>
          <input
            type="email"
            placeholder={t("login.email")}
            value={emailLogin.email}
            onChange={(e) => setEmailLogin({ ...emailLogin, email: e.target.value })}
            required
            className="ui-input"
            autoComplete="username"
          />

          <PasswordInput
            value={emailLogin.password}
            onChange={(e) => setEmailLogin({ ...emailLogin, password: e.target.value })}
            placeholder={t("login.password") || "Password"}
            required
            name="password"
            autoComplete="current-password"
          />

          <button type="submit" className="btn btn-primary" disabled={signingIn}>
            {signingIn ? t("login.signing_in") || "Signing in…" : t("login.sign_in") || "Sign in"}
          </button>

          <button type="button" className="btn btn-link" onClick={handleForgotPassword}>
            {t("login.forgot_password") || "Forgot password?"}
          </button>
        </form>
      )}

     
    </div>
    </div>
  );
};

export default Login;